import { DialogActions, DialogContent, MenuItem } from '@mui/material';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import breakpoints from '../../../design-system/breakpoints';
import { FieldArray, getIn } from 'formik';
import Colours from '../../../design-system/colours';
import OvTextField from '../atoms/OvTextField';
import Variables from '../../../design-system/variables';
import OvSelect from '../atoms/OvSelect';
import {
  NotificationActionType,
  NotificationButtonStyle,
} from '../../../common/model/dto/notification/notification-content';
import OvButton from '../atoms/OvButton';
import OvNoContent from './OvNoContent';

interface OvNotificationContentActionsStepProps {
  formik: any;
  onNext: () => void;
  onCancel: () => void;
  onBack: () => void;
}

const OvNotificationContentActionsStep: FC<
  OvNotificationContentActionsStepProps
> = ({ formik, onNext, onCancel, onBack }) => {
  const { t } = useTranslation();

  return (
    <form>
      <StyledDialogContent>
        <FieldArray name="actions">
          {({ push, remove, form }) => (
            <>
              {formik.values.actions && formik.values.actions.length > 0 ? (
                formik.values.actions.map((action: any, index: number) => (
                  <ActionContainer key={index}>
                    <StyledTextField
                      label="CTA"
                      name={`actions.${index}.cta`}
                      value={formik.values?.actions?.[index].cta}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        !!getIn(form.touched, `actions[${index}].cta`) &&
                        !!getIn(form.errors, `actions[${index}].cta`)
                      }
                      helperText={
                        getIn(form.touched, `actions[${index}].cta`) &&
                        getIn(form.errors, `actions[${index}].cta`)
                      }
                    />
                    <StyledTextField
                      label="Action URL"
                      name={`actions.${index}.action_url`}
                      value={formik.values.actions?.[index].action_url}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <StyledLabel>
                      {t(
                        'notificationContents.notificationActions.buttonStyle'
                      )}
                    </StyledLabel>
                    <StyledSelect
                      name={`actions.${index}.button_style`}
                      value={formik.values.actions?.[index].button_style}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    >
                      {Object.values(NotificationButtonStyle).map((style) => (
                        <MenuItem key={style} value={style}>
                          {style}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                    <StyledLabel>
                      {t('notificationContents.notificationActions.type')}
                    </StyledLabel>
                    <StyledSelect
                      name={`actions.${index}.type`}
                      value={formik.values.actions?.[index].type}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    >
                      {Object.values(NotificationActionType).map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                    <StyledLightOvButton onClick={() => remove(index)}>
                      {t('notificationContents.dialogs.removeActionButton')}
                    </StyledLightOvButton>
                  </ActionContainer>
                ))
              ) : (
                <OvNoContent>
                  {t('notificationContents.dialogs.emptyActionButtonList')}
                </OvNoContent>
              )}
              <StyledOvButton
                onClick={() =>
                  push({
                    cta: '',
                    button_style: NotificationButtonStyle.PRIMARY,
                    action_url: '',
                    type: NotificationActionType.accept,
                  })
                }
              >
                {t('notificationContents.dialogs.addActionButton')}
              </StyledOvButton>
            </>
          )}
        </FieldArray>
        <StyledDialogActions>
          <StyledLightOvButton onClick={onCancel}>
            {t('common.actions.cancel')}
          </StyledLightOvButton>
          <StyledLightOvButton onClick={onBack}>
            {t('common.actions.back')}
          </StyledLightOvButton>
          <StyledOvButton
            onClick={onNext}
            disabled={!formik.values.title || !formik.values.short_description}
          >
            {t('common.actions.next')}
          </StyledOvButton>
        </StyledDialogActions>
      </StyledDialogContent>
    </form>
  );
};

export default OvNotificationContentActionsStep;

const StyledDialogContent = styled(DialogContent)`
  && {
    width: 100%;
    min-width: 31.25rem !important;
    padding: 0.5rem 1.5rem !important;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 0.75rem;

    @media (min-width: ${breakpoints.sm}) {
      width: 34rem;
    }
  }
`;

const ActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid ${Colours.OV_BASE};
  padding-bottom: 1rem;
`;

const StyledTextField = styled(OvTextField)`
  && {
    flex: 1 0 100%;

    .MuiFormHelperText-root.Mui-error {
      background: ${Colours.OV_WHITE};
      margin: 0;
      padding: 0.25rem 0.75rem;
    }
    .MuiInputLabel-formControl {
      top: -0.2rem;
      opacity: 0.6;
    }
  }
`;

const StyledLabel = styled.label`
  font-size: ${Variables.fontSizes.MEDIUM};
  margin-right: 0.5rem;
`;

const StyledSelect = styled(OvSelect)`
  && {
    max-width: none;
    height: 3rem;

    &.MuiInputBase-root {
      font-size: 0.875rem;
      border-color: ${Colours.OV_LIGHT_GRAY};
      width: 100%;

      .MuiOutlinedInput-notchedOutline {
        border-color: ${Colours.OV_BASE};
      }
    }
  }
`;

const StyledLightOvButton = styled(OvButton)`
  && {
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    padding: 0 1rem;
    background-color: ${Colours.OV_WHITE};
    border: 1px solid rgba(1, 39, 70, 0.5);
    color: ${Colours.OV_BASE};
    font-weight: bold;
    text-transform: none;
    max-width: fit-content;
    &:hover {
      border: 1px solid ${Colours.OV_BASE};
      background-color: rgba(1, 39, 70, 0.04);
    }
  }
`;

const StyledOvButton = styled(OvButton)`
  && {
    padding: 0 1rem;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    margin-right: 0.75rem;
    transition: none;
    font-weight: bold;
    text-transform: none;
    margin-left: 1rem;
    max-width: fit-content;

    &:last-child {
      margin-right: 0;
    }
  }
`;

const StyledDialogActions = styled(DialogActions)`
  && {
    margin-top: 2rem;
    padding: 0 0 0.5rem 1.5rem;
  }
`;
